import React, { useState, useRef } from "react";
import {
  FileText,
  UploadCloud,
  CheckCircle2,
  AlertCircle,
  Eye,
  RefreshCw,
  X,
  ExternalLink,
  ShieldCheck,
  Award,
} from "lucide-react";
import { uploadPartnerDocument } from "../../api/partner/partnerApi";
import { DOCUMENT_TITLES, getRequiredDocuments } from "../../guards/partnerGuard";
import { showToast } from "../common/notification/Notification";
import "./PartnerGuardModal.css";

export const PartnerDocumentGuardOverlay = ({
  visible,
  userData,
  uploadedDocsMap = {},
  assetUrl = "",
  token,
  userId,
  onRefresh,
}) => {
  const serviceId = userData?.service_id || userData?.service?.id || "1";
  const requiredDocs = getRequiredDocuments(serviceId);

  const [selectedFiles, setSelectedFiles] = useState({});
  const [uploadingDocKey, setUploadingDocKey] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const fileInputRefs = useRef({});

  const anyUploaded =
    Object.keys(uploadedDocsMap).length > 0 ||
    requiredDocs.some((k) => Boolean(uploadedDocsMap[k]));

  if (!visible || anyUploaded || dismissed) return null;

  const handleFileChange = (docKey, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      showToast("File size exceeds maximum allowed limit of 10 MB.", {
        type: "error",
      });
      return;
    }

    setSelectedFiles((prev) => ({
      ...prev,
      [docKey]: file,
    }));
  };

  const handleUpload = async (docKey) => {
    const file = selectedFiles[docKey];
    if (!file) {
      showToast("Please choose a file before clicking upload.", {
        type: "warning",
      });
      return;
    }

    setUploadingDocKey(docKey);
    try {
      const response = await uploadPartnerDocument(
        token,
        userId || userData?.id,
        docKey,
        file
      );

      if (
        response?.statusType === "SUCCESS" ||
        response?.status === "SUCCESS"
      ) {
        showToast(
          `${DOCUMENT_TITLES[docKey] || docKey} uploaded successfully!`,
          { type: "success" }
        );
        setSelectedFiles((prev) => {
          const next = { ...prev };
          delete next[docKey];
          return next;
        });
        if (onRefresh) onRefresh();
      } else {
        const errorMsg =
          response?.text || response?.message || "Failed to upload document.";
        showToast(errorMsg, { type: "error" });
      }
    } catch (error) {
      console.error("Document upload failed:", error);
      showToast("Failed to upload document. Please try again.", {
        type: "error",
      });
    } finally {
      setUploadingDocKey(null);
    }
  };

  const getDocPreviewUrl = (doc) => {
    if (!doc) return "";
    const rawUrl = doc.document_url || doc.file_url || doc.url || "";
    if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) {
      return rawUrl;
    }
    const cleanAsset = (assetUrl || "").replace(/\/+$/, "");
    const cleanPath = rawUrl.replace(/^\/+/, "");
    return `${cleanAsset}/${cleanPath}`;
  };

  return (
    <>
      <div className="partner-guard-overlay-backdrop">
        <div className="partner-guard-modal partner-guard-modal--wide position-relative">
          {/* Close / Dismiss Button */}
          <button
            type="button"
            className="btn btn-sm btn-light rounded-circle position-absolute top-0 end-0 m-3 shadow-sm"
            style={{ width: "32px", height: "32px", zIndex: 10 }}
            onClick={() => setDismissed(true)}
            title="Close"
          >
            <X size={16} />
          </button>

          {/* Header Icon */}
          <div className="partner-guard-icon-circle partner-guard-icon-circle--amber">
            <FileText size={32} />
          </div>

          {/* Title */}
          <h2 className="partner-guard-title">
            Partner KYC Document Verification
          </h2>

          {/* Subtitle */}
          <p className="partner-guard-subtitle mb-3">
            Please upload at least <strong>one</strong> valid identity document ({requiredDocs.map(k => DOCUMENT_TITLES[k] || k).join(", ")}). Only one document is required to complete verification.
          </p>

          {/* Info Alert matching mobile */}
          <div className="alert alert-info py-2 px-3 small d-flex align-items-center gap-2 mb-3 text-start mx-auto" style={{ maxWidth: "600px" }}>
            <AlertCircle size={18} className="flex-shrink-0 text-primary" />
            <span>
              If you have already uploaded any one of the documents, please wait for Admin Approval.
            </span>
          </div>

          {/* Documents List */}
          <div className="partner-guard-modal-body">
            <div className="partner-guard-doc-list">
              {requiredDocs.map((docKey) => {
                const uploadedDoc = uploadedDocsMap[docKey];
                const isUploaded = Boolean(uploadedDoc);
                const selectedFile = selectedFiles[docKey];
                const isUploading = uploadingDocKey === docKey;
                const title = DOCUMENT_TITLES[docKey] || docKey.toUpperCase();

                return (
                  <div
                    key={docKey}
                    className={`partner-guard-doc-card ${
                      isUploaded
                        ? "partner-guard-doc-card--uploaded"
                        : "partner-guard-doc-card--missing"
                    }`}
                  >
                    <div className="partner-guard-doc-card-header">
                      <div className="partner-guard-doc-title-wrap">
                        <div className="partner-guard-doc-icon">
                          {isUploaded ? (
                            <CheckCircle2 size={22} />
                          ) : (
                            <FileText size={22} />
                          )}
                        </div>
                        <div>
                          <h4 className="partner-guard-doc-title">{title}</h4>
                          <p className="partner-guard-doc-desc">
                            {docKey === "reraregistration"
                              ? "State RERA Agent / Broker Registration Certificate"
                              : docKey === "pan"
                              ? "Valid Business or Personal PAN Card (PDF/Image)"
                              : "Valid Government Issued Aadhaar Card (PDF/Image)"}
                          </p>
                        </div>
                      </div>

                      <div>
                        {isUploaded ? (
                          <span className="partner-guard-badge partner-guard-badge--active">
                            <CheckCircle2 size={13} /> Uploaded
                          </span>
                        ) : (
                          <span className="partner-guard-badge partner-guard-badge--pending">
                            <AlertCircle size={13} /> Action Required
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions and Upload Trigger */}
                    <div className="partner-guard-doc-actions">
                      {isUploaded && (
                        <>
                          <button
                            type="button"
                            className="partner-guard-view-btn"
                            onClick={() =>
                              setViewingDoc({
                                title,
                                url: getDocPreviewUrl(uploadedDoc),
                              })
                            }
                          >
                            <Eye size={15} />
                            <span>View Document</span>
                          </button>

                          <button
                            type="button"
                            className="partner-guard-replace-btn"
                            onClick={() =>
                              fileInputRefs.current[docKey]?.click()
                            }
                          >
                            <UploadCloud size={14} />
                            <span>Re-upload</span>
                          </button>
                        </>
                      )}

                      {/* Hidden File Input */}
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                        className="d-none"
                        ref={(el) => (fileInputRefs.current[docKey] = el)}
                        onChange={(e) => handleFileChange(docKey, e)}
                      />

                      {/* If no file uploaded yet or replacing */}
                      {(!isUploaded || selectedFile) && (
                        <div className="d-flex align-items-center gap-2 flex-wrap w-100 mt-2">
                          {!selectedFile ? (
                            <button
                              type="button"
                              className="partner-guard-upload-btn"
                              onClick={() =>
                                fileInputRefs.current[docKey]?.click()
                              }
                            >
                              <UploadCloud size={16} />
                              <span>Choose {title} File</span>
                            </button>
                          ) : (
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span
                                className="badge bg-light text-dark border px-3 py-2"
                                style={{ fontSize: "0.85rem" }}
                              >
                                {selectedFile.name.length > 25
                                  ? `${selectedFile.name.slice(0, 22)}...`
                                  : selectedFile.name}
                              </span>

                              <button
                                type="button"
                                className="partner-guard-upload-btn"
                                onClick={() => handleUpload(docKey)}
                                disabled={isUploading}
                              >
                                {isUploading ? (
                                  <>
                                    <RefreshCw
                                      size={15}
                                      className="partner-spin"
                                    />
                                    <span>Uploading...</span>
                                  </>
                                ) : (
                                  <>
                                    <UploadCloud size={15} />
                                    <span>Confirm Upload</span>
                                  </>
                                )}
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-link text-danger p-0"
                                onClick={() => {
                                  setSelectedFiles((prev) => {
                                    const next = { ...prev };
                                    delete next[docKey];
                                    return next;
                                  });
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer Note */}
          <div className="partner-guard-modal-footer justify-content-between flex-wrap gap-2">
            <span className="text-muted" style={{ fontSize: "0.8125rem" }}>
              Only 1 document required. Encrypted for verified partner KYC.
            </span>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                onClick={() => setDismissed(true)}
              >
                Upload Later & Explore Workspace
              </button>
              <button
                type="button"
                className="partner-guard-btn-secondary"
                onClick={onRefresh}
              >
                <RefreshCw size={15} />
                <span>Refresh Status</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      {viewingDoc && (
        <div
          className="partner-doc-viewer-modal"
          onClick={() => setViewingDoc(null)}
        >
          <div
            className="partner-doc-viewer-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="partner-doc-viewer-header">
              <span className="fw-bold text-dark">{viewingDoc.title}</span>
              <div className="d-flex align-items-center gap-2">
                <a
                  href={viewingDoc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                >
                  <ExternalLink size={14} />
                  <span>Open Full Screen</span>
                </a>
                <button
                  type="button"
                  className="btn btn-sm btn-light p-1"
                  onClick={() => setViewingDoc(null)}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="partner-doc-viewer-body">
              {viewingDoc.url.toLowerCase().endsWith(".pdf") ? (
                <iframe
                  src={`${viewingDoc.url}#toolbar=0`}
                  title={viewingDoc.title}
                />
              ) : (
                <img src={viewingDoc.url} alt={viewingDoc.title} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PartnerDocumentGuardOverlay;
